import { formatTimestamp } from 'sharedUi/dateUtils';
import { useUserAuditNames } from '../../hooks/useUserLookup';

interface AuditInfoCardProps {
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  /** Extra classes (e.g. to span grid columns). */
  className?: string;
  /** 'panel' takes the eval/RAG surface (layered shadow, 14px); 'card' is the
   *  app-wide default and is unchanged. */
  surface?: 'card' | 'panel';
}

/**
 * Shared "Record Information" card for view/detail screens.
 *
 * Timestamps are rendered VERBATIM via `formatTimestamp` — backend stores IST
 * wall-clock time in a UTC-looking field, so we show the stored digits with no
 * +5:30 shift. Actor ids (createdBy/updatedBy) are user_master ids, resolved to
 * user display names (never a raw id).
 */
export function AuditInfoCard({ createdAt, updatedAt, createdBy, updatedBy, className, surface = 'card' }: AuditInfoCardProps) {
  const { createdByName, updatedByName } = useUserAuditNames(createdBy, updatedBy);
  const panel = surface === 'panel';

  return (
    <div className={`${panel
      ? 'ui-panel p-4'
      : 'ds-card p-4'} ${className || ''}`}>
      <h3 className={panel
        ? 'mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted'
        : 'text-sm font-medium text-ink mb-2.5 flex items-center gap-1.5'}>
        <i className={panel ? 'pi pi-clock text-[11px]' : 'pi pi-clock text-primary'} style={panel ? undefined : { fontSize: '0.875rem' }} aria-hidden />
        Record Information
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        <div>
          <label className="text-xs font-medium text-ink-muted block mb-0.5">Created At</label>
          <p className="text-sm text-ink">{formatTimestamp(createdAt)}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-ink-muted block mb-0.5">Created By</label>
          <p className="text-sm text-ink">{createdByName}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-ink-muted block mb-0.5">Updated At</label>
          <p className="text-sm text-ink">{formatTimestamp(updatedAt)}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-ink-muted block mb-0.5">Updated By</label>
          <p className="text-sm text-ink">{updatedByName}</p>
        </div>
      </div>
    </div>
  );
}

export default AuditInfoCard;
